import {
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Divider,
  Group,
  Rating,
  Stack,
  Text,
} from '@mantine/core'
import type { FeedbackWithRelations, VisitWithRelations } from '@markku/types'
import { formatDate, formatTime } from '@markku/utils'
import { Trans, useLingui } from '@lingui/react/macro'
import { IconClock, IconStarFilled } from '@tabler/icons-react'
import { useState } from 'react'
import { feedbackHooks } from '../../../lib/trpcHooks/feedback'
import { FeedbackModal } from '../FeedbackModal'

interface VisitSummaryCardProps {
  visit: VisitWithRelations
}

export function VisitSummaryCard({ visit }: VisitSummaryCardProps) {
  const { t } = useLingui()
  const [feedbackModalOpened, setFeedbackModalOpened] = useState(false)

  const { data: feedbacks } = feedbackHooks.useList({
    filters: { visitId: { eq: visit.id } },
  })
  const existingFeedback = feedbacks?.data?.[0] as
    | FeedbackWithRelations
    | undefined

  const visitDate = new Date(visit.startTime)
  const dayName = visitDate.toLocaleDateString('fi-FI', { weekday: 'short' })
  const dateStr = formatDate(visit.startTime)
  const startTimeStr = formatTime(visit.startTime)
  const endTimeStr = formatTime(visit.endTime)
  const dateTimeStr = t`${dayName} ${dateStr} at ${startTimeStr} - ${endTimeStr}`

  const technician = visit.visitAttendees?.[0]?.user

  return (
    <>
      <Card withBorder>
        <Text size="lg" fw={600} mb="md">
          <Trans>Visit summary</Trans>
        </Text>

        <Stack gap="sm">
          <Group gap="sm">
            <Box
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                backgroundColor: 'var(--mantine-color-gray-1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <IconClock size={18} color="var(--mantine-color-gray-6)" />
            </Box>
            <Text size="sm">{dateTimeStr}</Text>
          </Group>

          {technician && (
            <Group gap="sm">
              <Avatar size="sm" radius="xl">
                {technician.firstName[0]}
                {technician.lastName[0]}
              </Avatar>
              <Text size="sm">
                {technician.firstName} {technician.lastName[0]}.
              </Text>
            </Group>
          )}

          {existingFeedback ? (
            <>
              <Divider my="md" />
              <Stack gap="sm">
                <Group gap="sm" align="center">
                  <Text size="sm" fw={500}>
                    <Trans>Customer rating:</Trans>
                  </Text>
                  <Rating
                    value={existingFeedback.ratingValue}
                    readOnly
                    size="sm"
                  />
                  <Text size="xs" c="dimmed">
                    ({existingFeedback.ratingValue}/5)
                  </Text>
                </Group>

                {existingFeedback.feedbackTagMappings &&
                  existingFeedback.feedbackTagMappings.length > 0 && (
                    <Group gap="xs">
                      {existingFeedback.feedbackTagMappings.map((mapping) => (
                        <Badge
                          key={mapping.feedbackTagId}
                          size="sm"
                          variant="light"
                          color="blue"
                        >
                          {mapping.feedbackTag?.name}
                        </Badge>
                      ))}
                    </Group>
                  )}

                {existingFeedback.description && (
                  <Box>
                    <Text size="xs" c="dimmed" mb={4}>
                      <Trans>Customer comment:</Trans>
                    </Text>
                    <Text size="sm" c="dimmed" style={{ fontStyle: 'italic' }}>
                      "{existingFeedback.description}"
                    </Text>
                  </Box>
                )}
              </Stack>
            </>
          ) : (
            <>
              <Divider my="md" />
              <Stack gap="xs">
                <Text size="sm" c="dimmed">
                  <Trans>
                    How did the visit go? Your feedback helps us improve our
                    service.
                  </Trans>
                </Text>
                <Button
                  size="sm"
                  variant="filled"
                  leftSection={<IconStarFilled size={18} />}
                  onClick={() => setFeedbackModalOpened(true)}
                  fullWidth
                >
                  <Trans>Give feedback</Trans>
                </Button>
              </Stack>
            </>
          )}
        </Stack>
      </Card>

      <FeedbackModal
        opened={feedbackModalOpened}
        onClose={() => setFeedbackModalOpened(false)}
        visit={visit}
      />
    </>
  )
}
